import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '@modules/elements/services/alert.service';
import { TimeFormatPipe } from '@modules/elements/pipes/time-format.pipe';
import { RecordsService } from '@modules/records/services/records.service';
import { Record, RecordServer, RecordTask, RecordTaskProgress, RecordTaskType } from '@modules/records/types/record';
import { TaskDetailsComponent } from '../task-details/task-details.component';

@Component({
    selector: 'app-tasks-board',
    templateUrl: './tasks-board.component.html',
    styleUrl: './tasks-board.component.less',
    standalone: false
})
export class TasksBoardComponent implements OnInit, OnChanges, OnDestroy {

  // Inputs
  @Input() record?: Record;
  @Input() server?: RecordServer;

  // Public
  public taskGroups: {group: string, tasks: RecordTask[]}[] = [];
  public now = new Date();

  // Private
  private liveProgress = new Map<string, RecordTaskProgress>();
  private timer?: Subscription;
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private recordsService: RecordsService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private timeFormatPipe: TimeFormatPipe,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    this.recordsService.getTaskProgress()
      .pipe(takeUntil(this.alive))
      .subscribe(event => {
        if (event?.id !== this.record?.id || event?.serverId !== this.server?.id) { return; }
        this.liveProgress.set(event.taskKey, event);
        this.applyProgress();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('server' in changes) {
      this.parseTasks();
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Method
   */

  parseTasks(): void {
    const tasks = this.server?.tasks || [];
    const tasksWithGroup = tasks.filter(task => task.group);
    const tasksWithoutGroup = tasks.filter(task => !task.group);

    this.taskGroups = [
      {group: 'General', tasks: []},
      {group: 'MAX', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('max') && !task.key.startsWith('maxParity'))},
      {group: 'MAX Parity', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('maxParity') && !task.key.startsWith('maxParityOvertime'))},
      {group: 'VOD', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('vod'))},
      {group: 'Linear', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('linear'))},
      {group: 'Content Acquisition', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('contentAcquisition'))},
      {group: 'Archive', tasks: tasksWithoutGroup.filter(task => task.key.startsWith('archive') || task.key.startsWith('maxParityOvertime'))},
    ];
    const selectedTask = this.taskGroups.flatMap(group => group.tasks).map(task => task.key);
    this.taskGroups[this.getGroupIndex('General')].tasks = tasksWithoutGroup.filter(task => !selectedTask.includes(task.key));

    for (const task of tasksWithGroup) {
      const index = this.getGroupIndex(task.group!);
      if (index >= 0) {
        this.taskGroups[index].tasks.push(task);
      } else {
        this.taskGroups.push({group: task.group!, tasks: [task]});
      }
    }

    this.applyProgress();
  }

  /**
   * The record is refetched every 10s, but the database value it carries lags the agent by up to
   * 5s — so a live per cent already received over the socket wins until the task leaves inProgress.
   */
  applyProgress(): void {
    for (const group of this.taskGroups) {
      for (const task of group.tasks) {
        if (task.status !== 'inProgress') {
          this.liveProgress.delete(task.key);
          continue;
        }
        const percent = this.liveProgress.get(task.key)?.percent;
        if (percent !== undefined && percent > (task.progress ?? 0)) {
          task.progress = percent;
        }
      }
    }

    this.syncTimer();
  }

  /**
   * A 1s tick drives the live elapsed-time chip, so it only runs while something is in progress.
   */
  syncTimer(): void {
    const isRunning = this.taskGroups.some(group =>
      group.tasks.some(task => task.status === 'inProgress' && task.timeStart));

    if (isRunning && !this.timer) {
      this.now = new Date();
      this.timer = interval(1000)
        .pipe(takeUntil(this.alive))
        .subscribe(() => this.now = new Date());
    } else if (!isRunning && this.timer) {
      this.timer.unsubscribe();
      this.timer = undefined;
    }
  }

  /**
   * The detail payload of the last taskProgress message, but only while the task is still
   * running and only when the agent actually sent size information.
   */
  getProgressDetails(task: RecordTask): RecordTaskProgress | undefined {
    if (task.status !== 'inProgress') { return undefined; }
    const progress = this.liveProgress.get(task.key);
    return progress?.total ? progress : undefined;
  }

  formatAmount(value?: number, unit?: string): string {
    if (value === undefined || value === null) { return '\u2014'; }
    if (unit === 'bytes') {
      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
      let size = value;
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size = size / 1024;
        index++;
      }
      return `${size.toFixed(1)} ${units[index]}`;
    }
    return `${value.toLocaleString()}${unit ? ' ' + unit : ''}`;
  }

  formatRemaining(seconds?: number): string {
    if (seconds === undefined || seconds === null || seconds < 0) { return '\u2014'; }
    const now = new Date();
    return this.timeFormatPipe.transform(now, new Date(now.getTime() + seconds * 1000));
  }

  getGroupIndex(group: string): number {
    return this.taskGroups.findIndex(item => item.group === group);
  }

  getTaskTitle(type?: RecordTaskType): string | undefined {
    switch (type) {
      case 'updateCinedeckSettings': return 'Cinedeck update settings';
      case 'recording': return 'Recording';
      case 'vodTrim': return 'Trim VOD J2K';
      case 'vodTranscode': return 'Transcode VOD J2K';
      case 'vodMetadata': return 'Metadata VOD J2K';
      case 'vodUpload': return 'Upload VOD J2K';
      case 'maxParityTrim': return 'Trim MAX J2K Interlace';
      case 'maxTrim': return 'Trim MAX J2K Progressive';
      case 'maxParityTranscode': return 'Transcode MAX J2K Interlace';
      case 'maxTranscode': return 'Transcode MAX J2K Progressive';
      case 'maxParityMetadata': return 'Metadata MAX J2K Interlace';
      case 'maxMetadata': return 'Metadata MAX J2K Progressive';
      case 'maxParityUpload': return 'Upload MAX J2K Interlace';
      case 'maxUpload': return 'Upload MAX J2K Progressive';
      case 'maxParityOvertimeTrim': return 'Trim Overtime MAX J2K Interlace';
      case 'maxParityOvertimeMetadata': return 'Metadata Overtime MAX J2K Interlace';
      case 'linearTrim': return 'Trim Linear';
      case 'linearTranscode': return 'Transcode Linear';
      case 'linearFfmpegTranscode': return 'Transcode Linear FFmpeg';
      case 'contentAcquisitionTranscode': return 'Transcode Content Acquisition';
      case 'linearMetadata': return 'Metadata Linear';
      case 'linearUpload': return 'Upload Linear';
      case 'archiveUpload': return 'Upload Archives Main';
      case 'archiveOvertimeUpload': return 'Upload Archives Overtime';
      case 'c3Upload': return 'Upload C3';
      case 'c3ExternalOutput': return 'C3 Router';
      case 'sourceRouter': return 'Source Router';
      case 'backupUpload': return 'Upload Backups';
      case 'finished': return 'Finished';
      case 's3downloadVideo': return 'Download S3 Video';
      case 'loadVideoToPlayback': return 'Open video to playback';
      case 'vodStartManualUpload': return 'Approve VOD J2K Upload';
      case 'maxParityStartManualUpload': return 'Approve MAX J2K Interlace Upload';
      case 'maxStartManualUpload': return 'Approve MAX J2K Progressive Upload';
      case 'linearStartManualUpload': return 'Approve Linear Upload';
      case 'restripe': return 'Restripe';
      default:
        return type;
    }
    return type;
  }

  /**
   * Actions
   */

  editTask(task: RecordTask, server?: RecordServer): void {
    if (!server) { return; }
    const needs = (task.needs || []).map(type => {
      const dependency = server.tasks?.find(item => item.type === type);
      return {
        title: dependency?.name || this.getTaskTitle(type) || type,
        status: dependency?.status,
      };
    });
    this.dialog.open(TaskDetailsComponent, {
      data: { task, serverId: server.id, needs },
      disableClose: true,
      autoFocus: false
    });
  }

  resetTasks(task: RecordTask, server?: RecordServer): void {
    if (this.record?.id && server && task.id) {
      this.alertService.show(
        `Reset Task ${this.getTaskTitle(task.type)}`,
        'Would you like to reset current task?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id && task.id) {
          this.recordsService.resetTask(this.record.id, [server.id], task.id);
        }
      });
    }
  }

  restartTask(task: RecordTask, server?: RecordServer, restartOnlyThis: boolean = false): void {
    if (this.record?.id && server && task.id) {
      this.alertService.show(
        `Restart Task ${this.getTaskTitle(task.type)}`,
        'Would you like to restart current task?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id && task.id) {
          this.recordsService.restartTask(this.record.id, [server.id], task.id, restartOnlyThis);
        }
      });
    }
  }

  forceDoneTask(task: RecordTask, server?: RecordServer): void {
    if (this.record?.id && server && task.id) {
      this.alertService.show(
        `Done Task ${this.getTaskTitle(task.type)}`,
        'Would you like to force done current task?',
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0 && this.record?.id && task.id) {
          this.recordsService.forceDoneTask(this.record.id, [server.id], task.key);
        }
      });
    }
  }

}
